import { normalizeSchedules } from './multiSchedule'

/**
 * Review Screen Settings (timeout_window.review / edit) are configured once on Properties
 * but persisted on every schedule so participant apps and backend keep reading
 * metadata.schedule[].timeout_window without changes.
 */

export const mergeReviewIntoTimeoutWindow = (timeout_window, sourceTimeoutWindow) => {
  const next = { ...(timeout_window || {}) }
  const { review, edit } = sourceTimeoutWindow || {}

  if (review) {
    next.review = { ...review }
    if (edit !== undefined && edit !== null && !Number.isNaN(edit?.value)) {
      next.edit = { ...edit }
    } else {
      delete next.edit
    }
  } else {
    delete next.review
    delete next.edit
  }

  return Object.keys(next).length ? next : undefined
}

export const applyReviewSettingsToSchedule = (schedule, sourceSchedule) => {
  if (!schedule || typeof schedule !== 'object') return schedule
  const timeout_window = mergeReviewIntoTimeoutWindow(
    schedule.timeout_window,
    sourceSchedule?.timeout_window,
  )
  if (!timeout_window) {
    const next = { ...schedule }
    delete next.timeout_window
    return next
  }
  return { ...schedule, timeout_window }
}

/**
 * Copy review/edit timeout_window fields from the editor schedule onto every schedule.
 */
export const applyReviewSettingsToAllSchedules = (schedules, sourceSchedule) => {
  if (!sourceSchedule || typeof sourceSchedule !== 'object') {
    return normalizeSchedules(schedules)
  }
  return normalizeSchedules(schedules).map(schedule =>
    applyReviewSettingsToSchedule(schedule, sourceSchedule),
  )
}

/**
 * When loading multi-schedule instruments, seed each schedule from the first entry
 * that already has review settings (legacy payloads may only have one).
 */
export const hydrateSchedulesWithReviewSettings = schedules => {
  const list = normalizeSchedules(schedules)
  if (!list.length) return list

  const source =
    list.find(schedule => schedule?.timeout_window?.review != null) || list[0]

  return applyReviewSettingsToAllSchedules(list, source)
}

/**
 * Resolve review settings for Properties UI from the active editor schedule,
 * falling back to the first schedule in the confirmed list.
 */
export const resolveEditorReviewTimeoutWindow = (editorSchedule, confirmedSchedules = []) => {
  const editorList = normalizeSchedules(editorSchedule)
  const editorWithReview = editorList.find(schedule => schedule?.timeout_window?.review != null)
  if (editorWithReview?.timeout_window) {
    return editorWithReview.timeout_window
  }

  const fromList = normalizeSchedules(confirmedSchedules).find(
    schedule => schedule?.timeout_window?.review != null,
  )
  if (fromList?.timeout_window) {
    return fromList.timeout_window
  }

  return editorList[0]?.timeout_window || {}
}

/**
 * Choose which schedule supplies review/edit fields when fanning out on save.
 * Properties saves use the editor schedule (including explicit disable).
 * Schedule-only saves keep existing review settings when editing unrelated fields.
 */
export const resolveReviewSettingsSourceSchedule = (
  editorSchedule,
  schedules = [],
  { preferEditor = false } = {},
) => {
  if (preferEditor) {
    return editorSchedule
  }

  const fromSchedules = normalizeSchedules(schedules).find(
    schedule => schedule?.timeout_window?.review != null,
  )
  if (fromSchedules) {
    return fromSchedules
  }

  return editorSchedule
}
